import axios, { AxiosInstance } from 'axios';
import { getVoiceFallbackClient } from './_shared';

// ── Voice Management & Expansion (/api/voices) ──────────────────────────

export async function fetchVoiceCatalog(params?: { search?: string; gender?: string; language?: string; use_case?: string }, axiosInstance?: AxiosInstance): Promise<{ voices: any[]; total: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>('/api/voices/catalog', { params });
  return response.data;
}

export async function previewVoice(text: string, voice_id: string, axiosInstance?: AxiosInstance): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post('/api/voices/preview', { text, voice_id }, { responseType: 'blob' });
  return response.data;
}

export async function cloneVoice(name: string, workspace_id: string, files: File[], axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const formData = new FormData();
  formData.append('name', name);
  formData.append('workspace_id', workspace_id);
  files.forEach(file => formData.append('files', file));
  const response = await client.post('/api/voices/clone', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function fetchCustomVoices(workspace_id?: string, axiosInstance?: AxiosInstance): Promise<any[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>('/api/voices/custom', { params: { workspace_id } });
  return response.data;
}

export async function deleteCustomVoice(voice_id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`/api/voices/custom/${voice_id}`);
  return response.status === 200 || response.status === 204;
}

export async function setVoiceABConfig(campaign_id: string, config: any, axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.put('/api/voices/ab-config', { campaign_id, voice_ab_config: config });
  return response.data;
}

export async function deleteVoiceABConfig(campaign_id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`/api/voices/ab-config/${campaign_id}`);
  return response.status === 200 || response.status === 204;
}

export async function fetchVoiceABAnalytics(campaign_id: string, days: number = 30, axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get('/api/analytics/voice-ab', { params: { campaign_id, days } });
  return response.data;
}

export interface SmsAnalyticsPayload {
  total_sent: number;
  delivered: number;
  failed: number;
  pending: number;
  delivery_rate_pct: number;
  by_template: { template: string; sent: number; delivered: number; failed: number }[];
}

export async function fetchSmsAnalytics(
  params: { campaign_id?: string; days?: number },
  axiosInstance?: AxiosInstance
): Promise<SmsAnalyticsPayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<SmsAnalyticsPayload>('/api/analytics/sms', { params });
  return response.data;
}

export async function fetchVoicePresets(axiosInstance?: AxiosInstance): Promise<{ presets: any[] }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>('/api/voices/presets');
  return response.data;
}

export async function fetchAgentVoiceSettings(agent_id: string, axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`/api/voices/agent/${agent_id}/settings`);
  return response.data;
}

export async function updateAgentVoiceSettings(agent_id: string, settings: any, axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.put(`/api/voices/agent/${agent_id}/settings`, settings);
  return response.data;
}

export async function detectLanguage(text?: string, audio_url?: string, axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post('/api/voices/detect-language', { text, audio_url });
  return response.data;
}

export async function fetchSupportedLanguages(axiosInstance?: AxiosInstance): Promise<{ languages: string[] }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>('/api/voices/languages');
  return response.data;
}
